import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { workflowApi } from '../../services/api';
import type { Intervention } from '../../types';
import {
  Search,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Clock,
  Eye,
  Filter,
  User,
  Calendar,
} from 'lucide-react';

const INTERVENTION_TYPES: Record<string, { label: string; color: string }> = {
  annulation_client: { label: 'Annulation client', color: 'bg-red-100 text-red-800' },
  demande_documents: { label: 'Demande documents', color: 'bg-blue-100 text-blue-800' },
  informations_manquantes: { label: 'Informations manquantes', color: 'bg-yellow-100 text-yellow-800' },
  autre: { label: 'Autre', color: 'bg-gray-100 text-gray-800' },
};

const InterventionList: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [page, setPage] = useState(1);

  const { data, isLoading, error } = useQuery({
    queryKey: ['interventions-pending', searchQuery, typeFilter, page],
    queryFn: async () => {
      const response = await workflowApi.getInterventions({
        search: searchQuery || undefined,
        status: 'pending', // Only pending interventions
        type: typeFilter !== 'all' ? typeFilter : undefined,
        page,
        per_page: 20,
      });
      return response.data;
    },
  });

  const handleCustomerClick = (intervention: Intervention) => {
    if (intervention.customer) {
      navigate(`/workflow/customers/${intervention.customer.uuid}?fromState=${intervention.customer.state_before_intervention || intervention.customer.state}`);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div>
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Interventions en attente</h1>
        <p className="text-gray-600 mt-1">Demandes d'intervention conseiller à traiter</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un client..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
              />
            </div>

            {/* Type Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  setPage(1);
                }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              >
                <option value="all">Tous les types</option>
                <option value="annulation_client">Annulation client</option>
                <option value="demande_documents">Demande documents</option>
                <option value="informations_manquantes">Informations manquantes</option>
                <option value="autre">Autre</option>
              </select>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        {data && (
          <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 flex items-center justify-between">
            <span className="text-sm text-amber-800 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              {data.meta.total} intervention{data.meta.total !== 1 ? 's' : ''} en attente
            </span>
          </div>
        )}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="bg-white rounded-lg shadow p-12 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700 font-medium">Erreur lors du chargement des interventions</p>
          <p className="text-red-600 text-sm mt-1">{error instanceof Error ? error.message : 'Unknown error'}</p>
        </div>
      )}

      {/* Interventions List */}
      {data && !isLoading && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {data.data.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="font-medium">Aucune intervention en attente</p>
              <p className="text-sm mt-1">Toutes les demandes d'intervention ont été traitées</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {data.data.map((intervention) => {
                const typeConfig = INTERVENTION_TYPES[intervention.intervention_type] || INTERVENTION_TYPES.autre;

                return (
                  <div
                    key={intervention.id}
                    className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => handleCustomerClick(intervention)}
                  >
                    <div className="flex items-start justify-between gap-4">
                      {/* Left Section - Customer Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          {/* Customer Name */}
                          <span className="font-medium text-gray-900 truncate">
                            {intervention.customer?.fullname || 'Client inconnu'}
                          </span>

                          {/* Status Badge */}
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                            <Clock className="w-3 h-3" />
                            En attente
                          </span>

                          {/* Type Badge */}
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${typeConfig.color}`}>
                            {typeConfig.label}
                          </span>
                        </div>

                        {/* Message */}
                        <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                          {intervention.message}
                        </p>

                        {/* Meta Info */}
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          {/* Created By */}
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {intervention.created_by?.name || 'Inconnu'}
                          </span>

                          {/* Created Date */}
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(intervention.created_at)}
                          </span>

                          {/* Fondation */}
                          {intervention.customer?.fondation && (
                            <span className="text-gray-400">
                              {intervention.customer.fondation}
                            </span>
                          )}

                          {/* Original State */}
                          {intervention.customer?.state_before_intervention && (
                            <span className="text-gray-400">
                              État original: {intervention.customer.state_before_intervention}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Right Section - Actions */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCustomerClick(intervention);
                          }}
                          className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                          title="Voir le client"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {data.meta.last_page > 1 && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between bg-gray-50">
              <div className="text-sm text-gray-500">
                Page {data.meta.current_page} sur {data.meta.last_page}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setPage(Math.min(data.meta.last_page, page + 1))}
                  disabled={page === data.meta.last_page}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InterventionList;
